import React, { useState } from 'react';
import { Search, Filter, Plus, Eye, Pencil, Phone, Mail, Calendar, Star, Target, TrendingUp, User, MessageSquare, MoreVertical, RefreshCw, Trash2, Upload, ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import XLSX from 'xlsx-js-style';
import AddLeadModal from '../Modals/AddLeadModal';
import SalesLeadFilters from './SalesLeadFilters';
import LeadDetailModal from '../Modals/LeadDetailModal';
import EditLeadModal from '../Modals/EditLeadModal';
import ImportLeadsModal from '../Modals/ImportLeadsModal';
import { useLeads } from '../../hooks/useSupabase';
import { useAuth, mockUsers } from '../../contexts/AuthContext';
import { deleteLeadDirect } from '../../hooks/useSupabase';
import { supabase } from '../../lib/supabase';

interface SalesLeadsProps {
}

const dateBoundaryToIso = (date: string, endOfDay = false) => {
  if (!date) return undefined;
  const boundary = new Date(`${date}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(boundary.getTime()) ? undefined : boundary.toISOString();
};

const SalesLeads: React.FC<SalesLeadsProps> = () => {
  const { leads, loading, fetchLeads, addLead, updateLead, totalCount, totalPages } = useLeads();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [meetingTypeFilter, setMeetingTypeFilter] = useState<string>('all');
  const [createdByFilter, setCreatedByFilter] = useState<string>('all');
  const [programFilter, setProgramFilter] = useState<string>('');
  const [registrationYearFilters, setRegistrationYearFilters] = useState<string[]>([]);
  const [createdStartDate, setCreatedStartDate] = useState('');
  const [createdEndDate, setCreatedEndDate] = useState('');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [userBranchId, setUserBranchId] = useState<string | null>(null);
  const [userBranchName, setUserBranchName] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const itemsPerPage = 20;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportScope, setExportScope] = useState<'current' | 'all' | 'range'>('current');
  const [exportStartPage, setExportStartPage] = useState(1);
  const [exportEndPage, setExportEndPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch user's branch and profiles
  React.useEffect(() => {
    const fetchUserBranchAndProfiles = async () => {
      if (!user?.id) return;

      try {
        // Kullanıcının branch_id'sini profiles tablosundan çek
        const { data: profile } = await supabase
          .from('profiles')
          .select('branch_id, branches(name)')
          .eq('id', user.id)
          .maybeSingle();

        const branchId = profile?.branch_id || null;
        const branchName = profile?.branches && !Array.isArray(profile.branches) ? (profile.branches as any).name : null;

        setUserBranchId(branchId);
        setUserBranchName(branchName);

        // Aynı şubedeki tüm kullanıcıları çek
        if (branchId) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, name, role, unit_type, branch_id')
            .eq('branch_id', branchId)
            .eq('is_active', true);

          setProfiles(profilesData || []);
        }
      } catch (err) {
        console.error('Unexpected error fetching user branch:', err);
      }
    };

    fetchUserBranchAndProfiles();
  }, [user?.id]);

  // Fetch leads when filters change
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const options: any = {
        page: currentPage,
        itemsPerPage: itemsPerPage,
        search: searchTerm,
        status: statusFilter,
        meetingType: meetingTypeFilter,
      };

      if (createdByFilter !== 'all') {
        // "Danışman" filtresi genellikle atanan kişiyi ifade eder
        options.assignedTo = createdByFilter;
      }

      if (user?.role !== 'admin') {
        // For non-admins, apply branch/assignment logic
        // If user has a branch, filter by that branch
        if (user?.branchId) {
          options.branchId = user.branchId;
        } else {
          // If no branch, maybe fallback to assignedTo? 
          // Assuming user.id is consultant id.
          // IMPORTANT: If admin selected a specific consultant (createdByFilter), we honor that.
          // If NOT admin, we enforce their own assignment unless they are filtering?
          // Usually non-admins can only see their own leads anyway due to RLS or this logic.
          // If we want to allow non-admins to see "all" (if permitted), we might need to adjust.
          // But strict logic:
          if (!createdByFilter || createdByFilter === 'all') {
            options.assignedTo = user?.id;
          }
        }
      }

      if (programFilter) {
        options.program = programFilter;
      }

      if (registrationYearFilters.length > 0) {
        options.registrationYears = registrationYearFilters;
      }

      options.createdFrom = dateBoundaryToIso(createdStartDate);
      options.createdTo = dateBoundaryToIso(createdEndDate, true);

      fetchLeads(options);
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, statusFilter, meetingTypeFilter, createdByFilter, programFilter, registrationYearFilters, createdStartDate, createdEndDate, user, fetchLeads, refreshTrigger]);


  // Listen for new leads to update activities
  React.useEffect(() => {
    const handleNewLead = () => {
      // Refresh leads data
      setRefreshTrigger(prev => prev + 1);

      // Add new activity
      const newActivity = {
        id: Date.now(),
        type: 'lead_created',
        message: 'Yeni lead eklendi',
        time: 'Az önce',
        icon: Target,
        color: 'text-blue-600'
      };

      setRecentActivities(prev => [newActivity, ...prev.slice(0, 4)]);
    };

    const handleLeadStatusUpdate = (event: any) => {
      const { leadId, newStatus, source } = event.detail;

      if (source === 'proposal') {
        // Lead listesini yenile
        setRefreshTrigger(prev => prev + 1);

        // Aktivite ekle
        const lead = leads.find(l => l.id === leadId);
        if (lead) {
          const statusText = newStatus === 'accepted' ? 'Kabul Edildi' :
            newStatus === 'rejected' ? 'Reddedildi' :
              newStatus === 'interested' ? 'İlgili' :
                newStatus === 'contacted' ? 'İletişim Kuruldu' : newStatus;

          const newActivity = {
            id: Date.now(),
            type: 'lead_status_updated',
            message: `${lead.first_name} ${lead.last_name} durumu: ${statusText} (Teklif üzerinden)`,
            time: 'Az önce',
            icon: Target,
            color: newStatus === 'accepted' ? 'text-green-600' :
              newStatus === 'rejected' ? 'text-red-600' : 'text-blue-600'
          };

          setRecentActivities(prev => [newActivity, ...prev.slice(0, 4)]);
        }
      }
    };

    window.addEventListener('newLeadAdded', handleNewLead);
    window.addEventListener('leadStatusUpdated', handleLeadStatusUpdate);
    return () => {
      window.removeEventListener('newLeadAdded', handleNewLead);
      window.removeEventListener('leadStatusUpdated', handleLeadStatusUpdate);
    };
  }, [fetchLeads, leads]);

  // Ensure leads is always an array
  const safeLeads = leads || [];

  // Since we use server-side pagination/filtering, 'leads' contains only the relevant data for current page.
  // We alias it to userLeads/filteredLeads to maintain compatibility with remainder of component code that expects these variables.
  const filteredLeads = safeLeads;
  const paginatedLeads = safeLeads;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  // Stats hesapla (filtrelenmiş lead'ler üzerinden)
  // Stats calculations - NOTE: With server pagination, we only have stats for the CURRENT PAGE.
  // To get global stats, we would need separate queries. 
  // For 'Total Leads', we use totalCount from hook.
  // For others, we might show 0 or remove them until we implement a stats endpoint.
  const stats = {
    totalLeads: totalCount,
    contactedLeads: 0, // Placeholder as we can't count form partial data
    highPriorityLeads: 0,
    interestedLeads: 0,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new_lead': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'lead': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'contacted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'qualified': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'unqualified': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'converted': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new_lead': return 'Yeni Lead';
      case 'lead': return 'Yeni Lead';
      case 'called': return 'Arandı';
      case 'no_answer': return 'Cevapsız';
      case 'busy': return 'Meşgul';
      case 'meeting_scheduled': return 'Görüşme Sağlandı';
      case 'proposal_sent': return 'Teklif Gönderildi';
      default: return status;
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'high': return 'Yüksek';
      case 'medium': return 'Orta';
      case 'low': return 'Düşük';
      default: return priority;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getContactRoleColor = (contactRole: string, creatorName?: string) => {
    if (contactRole === 'student') {
      if (creatorName === 'Betul Özbey' || creatorName === 'Kaya Bayezit') {
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800';
      }
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
    }
    if (contactRole === 'guardian') {
      if (creatorName === 'Edanur Aydın') {
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
      }
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
    }
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const getServiceColor = (creatorName?: string) => {
    if (creatorName === 'Betul Özbey' || creatorName === 'Kaya Bayezit') {
      return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800';
    }
    if (creatorName === 'Edanur Aydın') {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
    }
    if (creatorName === 'Medine Güzel') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800';
    }
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
  };

  const getSourceText = (source: string) => {
    switch (source) {
      case 'website': return 'Website';
      case 'referral': return 'Referans';
      case 'social-media': return 'Sosyal Medya';
      case 'advertisement': return 'Reklam';
      case 'walk-in': return 'Ziyaret';
      case 'other': return 'Diğer';
      default: return source;
    }
  };

  // Scroll to top on page change
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Handle filter changes (Reset page)
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, meetingTypeFilter, createdByFilter, programFilter, registrationYearFilters, createdStartDate, createdEndDate]);

  const currentYear = new Date().getFullYear();
  const registrationYearOptions = Array.from({ length: 8 }, (_, index) => String(currentYear - 2 + index));

  const handleLeadAction = async (action: string, leadId: string) => {
    const lead = safeLeads.find(l => l.id === leadId);
    if (!lead) return;

    setSelectedLead(lead);

    switch (action) {
      case 'view':
        setShowDetailModal(true);
        break;
      case 'edit':
        setShowEditModal(true);
        break;
      case 'call':
        // Karel sistemi ile arama yap
        if (window.makeKarelCall && lead.phone) {
          const success = window.makeKarelCall(lead.phone);
          if (success) {
            console.log(`${lead.first_name} ${lead.last_name} aranıyor: ${lead.phone}`);

            // Arama aktivitesi kaydet
            const activity = {
              id: Date.now(),
              type: 'call_initiated',
              message: `${lead.first_name} ${lead.last_name} arandı (${lead.phone})`,
              time: 'Az önce',
              icon: Phone,
              color: 'text-green-600'
            };

            window.dispatchEvent(new CustomEvent('newActivity', { detail: activity }));
          } else {
            // Fallback: WhatsApp'a yönlendir
            const cleanPhone = lead.phone.replace(/\D/g, '');
            const message = encodeURIComponent(`Merhaba ${lead.first_name}, Pisa Educazione'den arıyorum. Size nasıl yardımcı olabilirim?`);
            window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
          }
        } else {
          alert('Telefon numarası bulunamadı.');
        }
        break;
      case 'email':
        if (lead.email) {
          const subject = encodeURIComponent('Pisa Educazione - Eğitim Danışmanlığı');
          const body = encodeURIComponent(`Merhaba ${lead.first_name},\n\nPisa Educazione'den size ulaşıyorum. İtalya'da eğitim fırsatları hakkında konuşmak isterim.\n\nSaygılarımla,\n${user?.name || 'Danışman'}`);
          window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`, '_blank');
        }
        break;
      case 'delete':
        setShowDeleteConfirm(true);
        break;
      case 'qualify':
        try {
          await updateLead(leadId, {
            status: 'qualified',
            updated_at: new Date().toISOString()
          });
          setRefreshTrigger(prev => prev + 1); // Refresh data
          alert('Lead başarıyla "Nitelikli" durumuna güncellendi!');
        } catch (error) {
          console.error('Lead güncelleme hatası:', error);
          alert('Lead güncellenirken hata oluştu.');
        }
        break;
      default:
        console.log(`Action: ${action} for lead: ${leadId}`);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      const { error } = await updateLead(leadId, {
        status: newStatus,
        updated_at: new Date().toISOString()
      });

      if (error) {
        console.error('Lead durum güncelleme hatası:', error);
        alert('Lead durumu güncellenirken hata oluştu.');
        return;
      }

      alert(`Lead durumu "${getStatusText(newStatus)}" olarak güncellendi!`);
      setRefreshTrigger(prev => prev + 1); // Refresh data
    } catch (error) {
      console.error('Lead durum güncelleme hatası:', error);
      alert('Lead durumu güncellenirken beklenmeyen bir hata oluştu.');
    }
  };

  const handleTrackingStatusChange = async (leadId: string, newStatus: string) => {
    // Debug: user.id değerini kontrol et
    console.log('handleTrackingStatusChange - user:', user);
    console.log('handleTrackingStatusChange - user?.id:', user?.id);

    if (!user?.id) {
      alert('Kullanıcı oturumu bulunamadı. Lütfen sayfayı yenileyip tekrar giriş yapın.');
      return;
    }

    try {
      const updates: Record<string, any> = {
        tracking_status: newStatus,
        updated_at: new Date().toISOString(),
        updated_by: user.id
      };


      if (newStatus === 'enrolled') {
        updates.is_registered = true;
        updates.status = 'enrolled';
      } else if (newStatus === 'new_lead' || newStatus === 'rejected') {
        updates.is_registered = false;
        updates.status = newStatus === 'rejected' ? 'rejected' : 'new_lead';
      }

      console.log('handleTrackingStatusChange - updates:', updates);

      const { error } = await updateLead(leadId, updates);

      if (error) {
        console.error('Durum takip güncelleme hatası:', error);
        alert('Durum güncellenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
        return;
      }

      // Eğer kayıt oldu durumuna çekiliyorsa ve daha önce proposal yoksa, otomatik proposal oluştur
      if (newStatus === 'enrolled') {
        const { data: currentLead } = await supabase.from('leads').select('*').eq('id', leadId).single();

        if (currentLead) {
          const { data: existingProposal } = await supabase
            .from('proposals')
            .select('id')
            .eq('lead_id', leadId)
            .maybeSingle();

          if (!existingProposal) {
            // Fiyat Ayrıştırma (Currency Parsing)
            const parsePrice = (val: any): number => {
              if (!val) return 0;
              let str = String(val).trim();
              str = str.replace(/[^0-9.,]/g, '');
              if (!str) return 0;
              // TR format (1.234,56) vs US/Generic format
              if (str.includes('.') && str.includes(',')) {
                if (str.lastIndexOf(',') > str.lastIndexOf('.')) str = str.replace(/\./g, '').replace(',', '.');
                else str = str.replace(/,/g, '');
              } else {
                const sep = str.includes('.') ? '.' : ',';
                const parts = str.split(sep);
                if (parts.length > 2 || (parts.length > 1 && parts[parts.length - 1].length === 3)) str = str.replace(new RegExp(`\\${sep}`, 'g'), '');
                else if (sep === ',') str = str.replace(',', '.');
              }
              return parseFloat(str) || 0;
            };

            const priceValue = parsePrice(currentLead.discussed_price);

            const cleanEmails = Array.isArray(currentLead.emails) ? currentLead.emails : [];
            const studentInfo = typeof currentLead.student_info === 'object' ? currentLead.student_info : {};
            const phoneNumber = currentLead.phone || studentInfo?.phone || 'Belirtilmedi';

            const validUntil = new Date();
            validUntil.setDate(validUntil.getDate() + 30);

            const proposalData = {
              lead_id: leadId,
              student_name: `${currentLead.first_name} ${currentLead.last_name}`,
              student_email: cleanEmails[0] || 'Belirtilmedi',
              university: 'Kayıt Oldu',
              program: 'Kayıt Oldu',
              degree: 'Kayıt Oldu',
              total_amount: priceValue,
              service_fee: priceValue,
              currency: 'EUR',
              status: 'accepted', // Kabul edildi olarak işaretle
              created_by: currentLead.created_by || user.id,
              consultant_name: user?.name || 'Bilinmiyor',
              valid_until: validUntil.toISOString(),
              notes: `Listeden otomatik oluşturuldu.\nÖğrenci: ${currentLead.first_name} ${currentLead.last_name}\nTelefon: ${phoneNumber}\nFiyat: ${priceValue} EUR`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            const { error: proposalError } = await supabase.from('proposals').insert(proposalData);
            if (proposalError) console.error('Otomatik proposal oluşturma hatası:', proposalError);
          }
        }
      }

      const statusText = newStatus === 'enrolled' ? 'Kayıt Oldu' :
        newStatus === 'rejected' ? 'Red' : 'Yeni Lead';
      alert(`Lead durumu "${statusText}" olarak güncellendi!`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error: any) {
      console.error('Durum takip güncelleme hatası:', error);
      alert('Beklenmeyen bir hata oluştu: ' + (error?.message || 'Bilinmeyen hata'));
    }
  };


  const handleDeleteLead = async () => {
    if (!selectedLead) return;

    try {
      // deleteLead fonksiyonunu useLeads hook'undan al
      const { error } = await deleteLeadDirect(selectedLead.id);

      if (error) {
        console.error('Lead silme hatası:', error);
        const detail = error.code === 'PGRST116'
          ? 'Kayıt bulunamadı veya bu kaydı silme yetkiniz yok.'
          : `${error.message || 'Bilinmeyen hata'}${error.code ? ` (Kod: ${error.code})` : ''}`;
        alert(`Lead silinirken hata oluştu. ${detail}`);
        return;
      }

      alert(`${selectedLead.first_name} ${selectedLead.last_name} başarıyla silindi!`);
      setShowDeleteConfirm(false);
      setSelectedLead(null);
      setRefreshTrigger(prev => prev + 1); // Listeyi yenile
    } catch (error) {
      console.error('Lead silme hatası:', error);
      alert('Lead silinirken beklenmeyen bir hata oluştu.');
    }
  };
  const handleSaveLead = async (leadData: any): Promise<{ ok: boolean; error?: string; data?: any }> => {
    try {
      const { data, error } = await addLead(leadData);

      if (error) {
        console.error('Lead ekleme hatası:', error);
        return { ok: false, error: error.message || 'Bilinmeyen hata' };
      }

      // Refresh the students list
      setRefreshTrigger(prev => prev + 1);
      return { ok: true, data };
    } catch (error: any) {
      console.error('Lead ekleme hatası:', error);
      // Lead başarıyla oluşturulmuşsa hata gösterme
      if (error?.code !== 'PGRST116') {
        return { ok: false, error: error?.message || 'Beklenmeyen bir hata oluştu' };
      }
      return { ok: true };
    }
  };

  const getLeadScore = (lead: any) => {
    return lead.lead_score || 0;
  };

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const handleEmailClick = (email: string) => {
    if (!email) return;

    // Gmail'de yeni mail oluştur
    const subject = encodeURIComponent('Pisa Educazione - Eğitim Danışmanlığı');
    const body = encodeURIComponent(`Merhaba,\n\nPisa Educazione'den size ulaşıyorum. İtalya'da eğitim fırsatları hakkında konuşmak isterim.\n\nSaygılarımla,\n${user?.name || 'Danışman'}`);

    // Gmail compose URL'si
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

    // Yeni sekmede aç
    window.open(gmailUrl, '_blank');
  };

  const handleImportLeads = async (importedLeads: any[]): Promise<{ ok: boolean; error?: string; successCount?: number; errorCount?: number }> => {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (const lead of importedLeads) {
      try {
        const { assigned_to, branch_id, ...leadData } = lead;

        const leadWithDefaults = {
          ...leadData,
          created_by: user?.id || null,
          status: lead.status || 'new_lead',
          priority: lead.priority || 'medium',
          source: lead.source || 'import',
          interested_countries: lead.interested_countries || ['İtalya'],
          interested_programs: lead.interested_programs || [],
          contact_role: lead.contact_role || 'student',
        };

        const { error } = await addLead(leadWithDefaults);
        if (error) {
          errorCount++;
          errors.push(`${lead.first_name} ${lead.last_name}: ${error.message}`);
          console.error('Error adding lead:', error);
        } else {
          successCount++;
        }
      } catch (error: any) {
        errorCount++;
        errors.push(`${lead.first_name} ${lead.last_name}: ${error.message}`);
        console.error('Exception adding lead:', error);
      }
    }

    setRefreshTrigger(prev => prev + 1);

    if (errorCount > 0) {
      console.error('Import errors:', errors);
    }

    return {
      ok: successCount > 0,
      successCount,
      errorCount,
      error: errorCount > 0 ? `${errorCount} lead eklenemedi` : undefined,
    };
  };

  const formatDateTimeForExport = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'dd/MM/yyyy HH:mm', { locale: tr });
  };

  const formatDateForExport = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'dd/MM/yyyy', { locale: tr });
  };

  const joinExportValue = (value: any) => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    return value || '';
  };

  const getTrackingStatusText = (status?: string) => {
    switch (status) {
      case 'enrolled': return 'Kayıt';
      case 'rejected': return 'Red';
      case 'new_lead': return 'Yeni Lead';
      default: return status || 'Yeni Lead';
    }
  };

  const getMeetingTypeText = (meetingType?: string) => {
    switch (meetingType) {
      case 'in-person': return 'Fiziki Görüşme';
      case 'phone': return 'Telefonda Görüşme';
      case 'video': return 'Online Görüşme';
      case 'whatsapp': return 'WhatsApp Mesajlaşma';
      case 'email': return 'E-mail';
      default: return meetingType || '';
    }
  };

  const getContactRoleText = (lead: any) => {
    if (lead.contact_role === 'guardian') {
      const relationship = lead.guardian_info?.relationship;
      const relationshipText = relationship === 'anne' ? 'Anne' :
        relationship === 'baba' ? 'Baba' :
          relationship === 'kardes' ? 'Kardeş' :
            relationship === 'diger' ? 'Diğer' : 'Veli';
      return `Veli - ${relationshipText}`;
    }
    return 'Öğrenci';
  };

  const getProfileName = (profileId?: string | null) => {
    if (!profileId) return '';
    return profiles.find(p => p.id === profileId)?.name || profileId;
  };

  const getConsultantNames = (consultantIds?: string[]) => {
    if (!Array.isArray(consultantIds)) return '';
    return consultantIds.map(id => getProfileName(id)).filter(Boolean).join(', ');
  };

  const buildLeadExportRow = (lead: any, index: number) => {
    const studentInfo = typeof lead.student_info === 'object' && lead.student_info ? lead.student_info : {};
    const guardianInfo = typeof lead.guardian_info === 'object' && lead.guardian_info ? lead.guardian_info : {};
    const creatorName = lead.creator?.name || getProfileName(lead.created_by) || 'Bilinmiyor';

    return {
      'Sıra': index + 1,
      'Lead ID': lead.id || '',
      'Ad': lead.first_name || '',
      'Soyad': lead.last_name || '',
      'Kişi Tipi': getContactRoleText(lead),
      'Email': joinExportValue(lead.emails) || lead.email || '',
      'Telefon': lead.phone || '',
      'Durum': getStatusText(lead.status || 'new_lead'),
      'Durum Takip': getTrackingStatusText(lead.tracking_status || lead.status),
      'Öncelik': getPriorityText(lead.priority || 'medium'),
      'Kaynak': getSourceText(lead.source || 'other'),
      'Lead Skoru': lead.lead_score || 0,
      'Kayıt Oldu': lead.is_registered ? 'Evet' : 'Hayır',
      'Kayıt Yılı': lead.registration_year || '',
      'İlgilenilen Hizmetler': joinExportValue(lead.interested_services),
      'İlgilenilen Ülkeler': joinExportValue(lead.interested_countries),
      'İlgilenilen Programlar': joinExportValue(lead.interested_programs),
      'İlgilenilen Üniversiteler': joinExportValue(lead.interested_universities),
      'Eğitim Seviyesi': lead.education_level || studentInfo.education_level || '',
      'İngilizce Seviyesi': lead.english_level || studentInfo.english_level || '',
      'İtalyanca Seviyesi': lead.italian_level || studentInfo.italian_level || '',
      'Öğrenci Adı': studentInfo.first_name || '',
      'Öğrenci Soyadı': studentInfo.last_name || '',
      'Öğrenci Telefon': studentInfo.phone || '',
      'Öğrenci Email': joinExportValue(studentInfo.emails),
      'Öğrenci Uyruk': studentInfo.nationality || '',
      'Veli Yakınlık': guardianInfo.relationship || '',
      'Görüşme Türü': getMeetingTypeText(lead.meeting_type),
      'Görüşme Tarihi': formatDateForExport(lead.meeting_date),
      'Görüşme Saati': lead.meeting_time || '',
      'Görüşme Danışmanı': getProfileName(lead.meeting_consultant),
      'Görüşme Özeti': lead.meeting_summary || '',
      'Konuşulan Fiyat': lead.discussed_price || '',
      'Takip Gerekli': lead.follow_up_required ? 'Evet' : 'Hayır',
      'Takip Adı': lead.follow_up_name || '',
      'Takip Soyadı': lead.follow_up_surname || '',
      'Takip Telefon': lead.follow_up_phone || '',
      'Takip Tarihi': formatDateForExport(lead.follow_up_date),
      'Takip Saati': lead.follow_up_time || '',
      'Takip Durumu': lead.follow_up_status || '',
      'Takip Adresi': lead.follow_up_address || '',
      'Takip Notları': lead.follow_up_notes || '',
      'Atanan Danışmanlar': getConsultantNames(lead.assigned_consultants),
      'Oluşturan': creatorName,
      'Oluşturulma Tarihi': formatDateTimeForExport(lead.created_at),
      'Son Güncelleme': formatDateTimeForExport(lead.updated_at),
      'Notlar': lead.notes || '',
    };
  };

  const applyExportFilters = (query: any) => {
    const term = searchTerm.trim();

    if (term) {
      const parts = term.split(/\s+/).filter(Boolean);
      const searchConditions = [
        `first_name.ilike.%${term}%`,
        `last_name.ilike.%${term}%`,
        `phone.ilike.%${term}%`,
        `student_info->>first_name.ilike.%${term}%`,
        `student_info->>last_name.ilike.%${term}%`
      ];

      if (parts.length >= 2) {
        const p1 = parts[0];
        const p2 = parts.slice(1).join(' ');
        searchConditions.push(`and(first_name.ilike.%${p1}%,last_name.ilike.%${p2}%)`);
        searchConditions.push(`and(last_name.ilike.%${p1}%,first_name.ilike.%${p2}%)`);
        searchConditions.push(`and(student_info->>first_name.ilike.%${p1}%,student_info->>last_name.ilike.%${p2}%)`);
        searchConditions.push(`and(student_info->>last_name.ilike.%${p1}%,student_info->>first_name.ilike.%${p2}%)`);
      }

      query = query.or(searchConditions.join(','));
    }

    if (statusFilter !== 'all') query = query.eq('status', statusFilter);
    if (meetingTypeFilter !== 'all') query = query.eq('meeting_type', meetingTypeFilter);

    if (createdByFilter !== 'all') {
      query = query.contains('assigned_consultants', [createdByFilter]);
    }

    if (user?.role !== 'admin') {
      if (user?.branchId) {
        query = query.eq('branch_id', user.branchId);
      } else if (!createdByFilter || createdByFilter === 'all') {
        query = query.contains('assigned_consultants', [user?.id]);
      }
    }

    if (programFilter) query = query.contains('interested_programs', [programFilter]);
    if (registrationYearFilters.length > 0) query = query.in('registration_year', registrationYearFilters);
    const createdFrom = dateBoundaryToIso(createdStartDate);
    const createdTo = dateBoundaryToIso(createdEndDate, true);
    if (createdFrom) query = query.gte('created_at', createdFrom);
    if (createdTo) query = query.lte('created_at', createdTo);

    return query;
  };

  const fetchLeadsForExport = async () => {
    if (exportScope === 'current') return paginatedLeads;

    const from = exportScope === 'range' ? (exportStartPage - 1) * itemsPerPage : 0;
    const to = exportScope === 'range'
      ? Math.min(exportEndPage * itemsPerPage - 1, Math.max(totalCount - 1, 0))
      : Math.max(totalCount - 1, 0);

    if (to < from) return [];

    const pageSize = 1000;
    const requests = [];

    for (let start = from; start <= to; start += pageSize) {
      const end = Math.min(start + pageSize - 1, to);
      const query = applyExportFilters(
        supabase
          .from('leads')
          .select(`*, creator:profiles!leads_created_by_fkey(name)`)
      )
        .order('created_at', { ascending: false })
        .range(start, end);
      requests.push(query);
    }

    const results = await Promise.all(requests);
    const errors = results.filter(result => result.error).map(result => result.error);
    if (errors.length > 0) throw errors[0];

    return results.flatMap(result => result.data || []);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);

    try {
      const leadsForExport = await fetchLeadsForExport();

      if (leadsForExport.length === 0) {
        alert('Dışa aktarılacak lead bulunamadı.');
        return;
      }

      const dataToExport = leadsForExport.map(buildLeadExportRow);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
      const exportHeaders = Object.keys(dataToExport[0]);
      const thinBorder = {
        top: { style: 'thin', color: { rgb: 'D7DEE8' } },
        bottom: { style: 'thin', color: { rgb: 'D7DEE8' } },
        left: { style: 'thin', color: { rgb: 'D7DEE8' } },
        right: { style: 'thin', color: { rgb: 'D7DEE8' } },
      };

      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
      worksheet['!cols'] = exportHeaders.map((key) => ({
        wch: Math.min(Math.max(key.length + 4, 14), key.includes('Not') || key.includes('Özet') ? 46 : 32)
      }));
      worksheet['!rows'] = [{ hpt: 32 }, ...leadsForExport.map(() => ({ hpt: 24 }))];

      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          fill: { patternType: 'solid', fgColor: { rgb: '17324D' } },
          border: thinBorder,
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
        };
      }

      const centeredHeaders = new Set(['Sıra', 'Lead Skoru', 'Kayıt Oldu', 'Kayıt Yılı', 'Durum', 'Durum Takip', 'Öncelik', 'Takip Gerekli']);
      for (let row = 1; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const address = XLSX.utils.encode_cell({ r: row, c: col });
          const cell = worksheet[address];
          if (!cell) continue;
          const header = exportHeaders[col];
          const value = String(cell.v ?? '');
          const positive = ['Kayıt', 'Evet', 'İletişim Kuruldu', 'İlgili', 'Tamamlandı'].includes(value);
          const negative = ['Red', 'Hayır', 'Reddedildi'].includes(value);
          cell.s = {
            font: { color: { rgb: positive ? '166534' : negative ? '991B1B' : '1F2937' } },
            fill: { patternType: 'solid', fgColor: { rgb: positive ? 'ECFDF5' : negative ? 'FEF2F2' : row % 2 === 0 ? 'F8FAFC' : 'FFFFFF' } },
            border: thinBorder,
            alignment: {
              horizontal: centeredHeaders.has(header) ? 'center' : 'left',
              vertical: 'center',
              wrapText: header.includes('Not') || header.includes('Özet'),
            },
          };
        }
      }

      const summaryData = [
        { 'Alan': 'Rapor', 'Değer': 'Satış Leadleri' },
        { 'Alan': 'Kapsam', 'Değer': exportScope === 'current' ? `Mevcut sayfa (${currentPage})` : exportScope === 'all' ? 'Tüm filtrelenmiş kayıtlar' : `Sayfa ${exportStartPage} - ${exportEndPage}` },
        { 'Alan': 'Lead Sayısı', 'Değer': leadsForExport.length },
        { 'Alan': 'Oluşturulma', 'Değer': format(new Date(), 'dd/MM/yyyy HH:mm', { locale: tr }) },
        { 'Alan': 'Arama', 'Değer': searchTerm || 'Yok' },
        { 'Alan': 'Durum', 'Değer': statusFilter === 'all' ? 'Tüm durumlar' : getStatusText(statusFilter) },
        { 'Alan': 'Görüşme Türü', 'Değer': meetingTypeFilter === 'all' ? 'Tüm görüşme türleri' : getMeetingTypeText(meetingTypeFilter) },
        { 'Alan': 'Danışman', 'Değer': createdByFilter === 'all' ? 'Tüm danışmanlar' : getProfileName(createdByFilter) },
        { 'Alan': 'Oluşturulma Tarihi', 'Değer': createdStartDate || createdEndDate ? `${createdStartDate || 'Başlangıç yok'} - ${createdEndDate || 'Bitiş yok'}` : 'Tüm tarihler' },
        { 'Alan': 'Program', 'Değer': programFilter || 'Tüm programlar' },
        { 'Alan': 'Kayıt Yılı', 'Değer': registrationYearFilters.length > 0 ? registrationYearFilters.join(', ') : 'Tüm yıllar' },
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet([
        ['Satış Leadleri Raporu'],
        ['Aktif filtrelere göre hazırlanmış dışa aktarım özeti'],
        [],
        ['Alan', 'Değer'],
        ...summaryData.map((item) => [item.Alan, item.Değer]),
      ]);
      summarySheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
      ];
      summarySheet['!cols'] = [{ wch: 22 }, { wch: 44 }];
      summarySheet['!rows'] = [{ hpt: 34 }, { hpt: 22 }, { hpt: 10 }, { hpt: 28 }];
      if (summarySheet.A1) summarySheet.A1.s = {
        font: { bold: true, sz: 20, color: { rgb: 'FFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: '17324D' } },
        alignment: { horizontal: 'left', vertical: 'center' },
      };
      if (summarySheet.A2) summarySheet.A2.s = {
        font: { italic: true, color: { rgb: 'D9E7F5' } },
        fill: { patternType: 'solid', fgColor: { rgb: '17324D' } },
      };
      ['A4', 'B4'].forEach((address) => {
        if (summarySheet[address]) summarySheet[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { patternType: 'solid', fgColor: { rgb: '0F766E' } }, border: thinBorder,
          alignment: { horizontal: 'left', vertical: 'center' },
        };
      });
      summaryData.forEach((_, index) => {
        const row = index + 4;
        ['A', 'B'].forEach((column) => {
          const cell = summarySheet[`${column}${row + 1}`];
          if (!cell) return;
          cell.s = {
            font: { bold: column === 'A', color: { rgb: '1F2937' } },
            fill: { patternType: 'solid', fgColor: { rgb: index % 2 === 0 ? 'F1F5F9' : 'FFFFFF' } },
            border: thinBorder,
            alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
          };
        });
        summarySheet['!rows']![row] = { hpt: 23 };
      });

      const workbook = XLSX.utils.book_new();
      workbook.Props = {
        Title: 'Satış Leadleri',
        Subject: 'Detaylı lead dışa aktarımı',
        Author: 'Pisa Panel',
        CreatedDate: new Date()
      };
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Özet');
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Lead Detayları');

      const scopeLabel = exportScope === 'current' ? `sayfa_${currentPage}` : exportScope === 'range' ? `sayfa_${exportStartPage}-${exportEndPage}` : 'tum_filtreler';
      const fileName = `satis_leadleri_${scopeLabel}_${format(new Date(), 'dd-MM-yyyy_HH-mm')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      setShowExportModal(false);
    } catch (error: any) {
      console.error('Lead export hatası:', error);
      alert(`Excel oluşturulurken hata oluştu: ${error?.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sales Leads</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Toplam {totalCount} lead • Sayfa {currentPage} / {totalPages} • Veritabanından canlı veri
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            disabled={loading}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
          <button
            onClick={() => {
              setExportScope('current');
              setExportStartPage(currentPage);
              setExportEndPage(currentPage);
              setShowExportModal(true);
            }}
            disabled={totalCount === 0}
            className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span>Dışa Aktar</span>
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>İçe Aktar</span>
          </button>
          <button
            onClick={() => setShowAddLeadModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Yeni Lead</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Lead</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Phone className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.contactedLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">İletişim Kuruldu</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Star className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.highPriorityLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Yüksek Öncelik</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.interestedLeads}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">İlgili Lead</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="relative min-w-0 flex-1 basis-64">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            aria-label="Lead ara"
            placeholder="İsim, telefon, email veya öğrenci bilgisi ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
          />
        </div>
        <SalesLeadFilters
          value={{
            status: statusFilter,
            meetingType: meetingTypeFilter,
            consultant: createdByFilter,
            program: programFilter,
            years: registrationYearFilters,
            createdFrom: createdStartDate,
            createdTo: createdEndDate,
          }}
          onApply={filters => {
            setStatusFilter(filters.status);
            setMeetingTypeFilter(filters.meetingType);
            setCreatedByFilter(filters.consultant);
            setProgramFilter(filters.program);
            setRegistrationYearFilters(filters.years);
            setCreatedStartDate(filters.createdFrom);
            setCreatedEndDate(filters.createdTo);
            setCurrentPage(1);
          }}
          profiles={profiles}
          yearOptions={registrationYearOptions}
          showProgram={Boolean(userBranchName?.includes('Kadıköy') || userBranchId === 'f5aa9c77-6958-4be5-8800-3007ce6c4f77')}
        />
      </div>

      {/* Leads Table */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Lead'ler yükleniyor...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Lead</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İletişim</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Oluşturan</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Görüşme Türü</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İlgilendiği Hizmet</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İlgilendiği Program</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Durum Takip</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Oluşturulma</th>
                    <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedLeads.map((lead) => {
                    const leadScore = getLeadScore(lead);

                    return (
                      <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="py-4 px-6">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {(lead.first_name?.[0] || '') + (lead.last_name?.[0] || '')}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {lead.first_name} {lead.last_name}
                              </p>
                              <div className="flex items-center space-x-2">
                                {lead.contact_role === 'guardian' && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactRoleColor('guardian', lead.creator?.name)}`}>
                                    Veli - {lead.guardian_info?.relationship === 'anne' ? 'Anne' :
                                      lead.guardian_info?.relationship === 'baba' ? 'Baba' :
                                        lead.guardian_info?.relationship === 'kardes' ? 'Kardeş' :
                                          lead.guardian_info?.relationship === 'diger' ? 'Diğer' :
                                            'Veli'}
                                  </span>
                                )}
                                {lead.contact_role === 'student' && (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactRoleColor('student', lead.creator?.name)}`}>
                                    Öğrenci
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="space-y-1">
                            {lead.emails && lead.emails.length > 0 && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                <Mail className="w-4 h-4" />
                                <button
                                  onClick={() => handleEmailClick(lead.emails?.[0] || '')}
                                  className="truncate max-w-48 hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors cursor-pointer"
                                  title="Gmail'de yeni mail oluştur"
                                >
                                  {lead.emails[0]}
                                </button>
                                {lead.emails && lead.emails.length > 1 && (
                                  <span className="text-xs text-gray-500">+{lead.emails.length - 1}</span>
                                )}
                              </div>
                            )}
                            {lead.phone && (
                              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                <Phone className="w-4 h-4" />
                                <span>{lead.phone}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center">
                            {(() => {
                              const creatorName = lead.created_by ?
                                (lead.creator?.name ||
                                  profiles.find(p => p.id === lead.created_by)?.name ||
                                  mockUsers.find(u => u.id === lead.created_by)?.name ||
                                  'Bilinmiyor') :
                                'Sistemden Yüklendi';

                              const getCreatorColor = (name: string) => {
                                if (name === 'Sistemden Yüklendi') return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-800' };
                                if (name === 'Bilinmiyor') return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-700' };

                                if (name === 'Betul Özbey' || name === 'Kaya Bayezit') {
                                  return { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-700 dark:text-cyan-300', border: 'border-cyan-200 dark:border-cyan-800' };
                                }
                                if (name === 'Edanur Aydın') {
                                  return { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-800' };
                                }
                                if (name === 'Medine Güzel') {
                                  return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800' };
                                }

                                return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' };
                              };

                              const colors = getCreatorColor(creatorName);

                              return (
                                <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
                                  {creatorName}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {lead.meeting_type === 'in-person' ? 'Fiziki Görüşme' :
                              lead.meeting_type === 'phone' ? 'Telefonda Görüşme' :
                                lead.meeting_type === 'video' ? 'Online Görüşme' :
                                  lead.meeting_type === 'whatsapp' ? 'WhatsApp Mesajlaşma' :
                                    lead.meeting_type === 'email' ? 'E-mail' : '-'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {lead.interested_services && Array.isArray(lead.interested_services) && lead.interested_services.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {lead.interested_services.slice(0, 2).map((service: string, idx: number) => (
                                  <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getServiceColor(lead.creator?.name)}`}>
                                    {service === 'language_courses' ? 'Dil' :
                                      service === 'university_application' ? 'Ünv.' :
                                        service === 'visa_support' ? 'Vize' :
                                          service === 'accommodation' ? 'Kon.' :
                                            service === 'health_insurance' ? 'Sig.' :
                                              service === 'exam_prep' ? 'Sınav' : service}
                                  </span>
                                ))}
                                {lead.interested_services.length > 2 && (
                                  <span className="text-xs text-gray-500">+{lead.interested_services.length - 2}</span>
                                )}
                              </div>
                            ) : '-'}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {lead.interested_programs && Array.isArray(lead.interested_programs) && lead.interested_programs.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {lead.interested_programs.slice(0, 1).map((prog: string, idx: number) => (
                                  <span key={idx} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getServiceColor(lead.creator?.name)}`}>
                                    {prog}
                                  </span>
                                ))}
                                {lead.interested_programs.length > 1 && (
                                  <span className="text-xs text-gray-500">+{lead.interested_programs.length - 1}</span>
                                )}
                              </div>
                            ) : '-'}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center">
                            <select
                              value={lead.tracking_status || 'new_lead'}
                              onChange={(e) => handleTrackingStatusChange(lead.id, e.target.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-lg border cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${lead.tracking_status === 'enrolled' ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' :
                                lead.tracking_status === 'rejected' ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800' :
                                  'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
                                }`}
                            >
                              <option value="new_lead">Yeni Lead</option>
                              <option value="enrolled">Kayit Oldu</option>
                              <option value="rejected">Red</option>
                            </select>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {lead.meeting_date ? (
                              <>
                                {format(new Date(lead.meeting_date), 'dd MMM yyyy', { locale: tr })}
                                {lead.meeting_time && (
                                  <span className="block text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                    {lead.meeting_time}
                                  </span>
                                )}
                              </>
                            ) : (
                              format(new Date(lead.created_at), 'dd MMM yyyy', { locale: tr })
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleLeadAction('view', lead.id)}
                              className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Detayları Görüntüle"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleLeadAction('edit', lead.id)}
                              className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              title="Düzenle"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {lead.status === 'new_lead' && (
                              <button
                                onClick={() => handleLeadAction('qualify', lead.id)}
                                className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                title="Nitelikli Durumuna Çevir"
                              >
                                <TrendingUp className="w-4 h-4" />
                              </button>
                            )}
                            <div className="relative">
                              <button
                                onClick={() => handleLeadAction('delete', lead.id)}
                                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                title="Lead'i Sil"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {
        !loading && filteredLeads.length > 0 && totalPages > 1 && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Gösterilen: {startIndex + 1}-{endIndex} / {totalCount} lead
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex items-center space-x-1">
                  {/* İlk sayfa */}
                  {currentPage > 3 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        1
                      </button>
                      {currentPage > 4 && (
                        <span className="text-gray-400 px-2">...</span>
                      )}
                    </>
                  )}

                  {/* Önceki sayfa */}
                  {currentPage > 1 && (
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      {currentPage - 1}
                    </button>
                  )}

                  {/* Mevcut sayfa */}
                  <button
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg font-medium"
                  >
                    {currentPage}
                  </button>

                  {/* Sonraki sayfa */}
                  {currentPage < totalPages && (
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      {currentPage + 1}
                    </button>
                  )}

                  {/* Son sayfa */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <span className="text-gray-400 px-2">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                Sayfa {currentPage} / {totalPages}
              </div>
            </div>
          </div>
        )
      }

      {
        !loading && filteredLeads.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-gray-500 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'Lead bulunamadı' : 'Henüz lead yok'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm
                ? 'Arama kriterlerinizi değiştirmeyi deneyin'
                : 'İlk lead\'inizi ekleyerek başlayın'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowAddLeadModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                İlk Lead'i Ekle
              </button>
            )}
          </div>
        )
      }

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm px-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
            <div className="flex items-start justify-between border-b border-gray-200 dark:border-gray-800 px-6 py-5">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Leadleri Dışa Aktar</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  İndirmek istediğiniz sayfa kapsamını seçin. Excel dosyası tüm lead detaylarıyla oluşturulur.
                </p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                disabled={isExporting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <label className={`block cursor-pointer rounded-xl border p-4 transition-colors ${exportScope === 'current' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-200 hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700'}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="exportScope"
                    checked={exportScope === 'current'}
                    onChange={() => {
                      setExportScope('current');
                      setExportStartPage(currentPage);
                      setExportEndPage(currentPage);
                    }}
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Mevcut sayfa</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Sayfa {currentPage} içindeki {paginatedLeads.length} lead dışa aktarılır.
                    </div>
                  </div>
                </div>
              </label>

              <label className={`block cursor-pointer rounded-xl border p-4 transition-colors ${exportScope === 'all' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-200 hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700'}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="exportScope"
                    checked={exportScope === 'all'}
                    onChange={() => setExportScope('all')}
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Tüm filtrelenmiş kayıtlar</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Aktif filtrelere uyan toplam {totalCount} lead tek Excel dosyasına alınır.
                    </div>
                  </div>
                </div>
              </label>

              <div className={`rounded-xl border p-4 transition-colors ${exportScope === 'range' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-gray-200 dark:border-gray-800'}`}>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="radio"
                    name="exportScope"
                    checked={exportScope === 'range'}
                    onChange={() => setExportScope('range')}
                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Sayfa aralığı</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Belirlediğiniz sayfalar arasındaki leadler dışa aktarılır.
                    </div>
                  </div>
                </label>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Başlangıç
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={Math.max(totalPages, 1)}
                      value={exportStartPage}
                      onFocus={() => setExportScope('range')}
                      onChange={(event) => {
                        const value = Math.min(Math.max(Number(event.target.value) || 1, 1), Math.max(totalPages, 1));
                        setExportStartPage(value);
                        if (value > exportEndPage) setExportEndPage(value);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Bitiş
                    </label>
                    <input
                      type="number"
                      min={exportStartPage}
                      max={Math.max(totalPages, 1)}
                      value={exportEndPage}
                      onFocus={() => setExportScope('range')}
                      onChange={(event) => {
                        const value = Math.min(Math.max(Number(event.target.value) || exportStartPage, exportStartPage), Math.max(totalPages, 1));
                        setExportEndPage(value);
                      }}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Dosyada <strong>Özet</strong> ve <strong>Lead Detayları</strong> sayfaları bulunur; detay sayfasında filtre satırı, geniş kolonlar ve iletişim, görüşme, takip, program, kayıt ve not alanları yer alır.
                <div className="mt-2 border-t border-gray-200 pt-2 text-xs dark:border-gray-700">
                  Aktif tarih filtresi: <strong>{createdStartDate || 'Başlangıç yok'} — {createdEndDate || 'Bitiş yok'}</strong>. Danışman, durum, arama, program ve kayıt yılı filtreleri de çıktıya uygulanır.
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-800 dark:bg-gray-900/60 sm:flex-row sm:justify-end">
              <button
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                İptal
              </button>
              <button
                onClick={handleExportToExcel}
                disabled={isExporting || totalCount === 0 || (exportScope === 'range' && exportStartPage > exportEndPage)}
                className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Hazırlanıyor</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Excel İndir</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onSave={handleSaveLead}
      />

      {/* Import Leads Modal */}
      <ImportLeadsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportLeads}
      />

      {/* Lead Detail Modal */}
      {
        selectedLead && (
          <LeadDetailModal
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedLead(null);
            }}
            lead={selectedLead}
            onEdit={() => {
              setShowDetailModal(false);
              setShowEditModal(true);
            }}
            onUpdateLead={(id, updates) => updateLead(id, updates)}
          />
        )
      }

      {/* Edit Lead Modal */}
      {
        selectedLead && (
          <EditLeadModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedLead(null);
            }}
            lead={selectedLead}
            onSave={async (updatedData) => {
              try {
                const { data, error } = await updateLead(selectedLead.id, updatedData);

                if (error) {
                  console.error('Lead güncelleme hatası:', error);
                  return { ok: false, error: 'Lead güncellenirken hata oluştu.' };
                }

                alert(`${selectedLead.first_name} ${selectedLead.last_name} başarıyla güncellendi!`);
                setRefreshTrigger(prev => prev + 1); // Listeyi yenile
                return { ok: true, data };
              } catch (error) {
                console.error('Lead güncelleme hatası:', error);
                return { ok: false, error: 'Beklenmeyen bir hata oluştu.' };
              }
            }}
          />
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && selectedLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Lead'i Sil</h3>
                    <p className="text-gray-600 dark:text-gray-400">Bu işlem geri alınamaz</p>
                  </div>
                </div>

                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  <strong>{selectedLead.first_name} {selectedLead.last_name}</strong> adlı lead'i kalıcı olarak silmek istediğinize emin misiniz?
                  Bu işlem geri alınamaz ve tüm veriler kaybolacaktır.
                </p>

                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setSelectedLead(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteLead}
                    className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Sil</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default SalesLeads;
